import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import { site, stats, principles, testimonials, brandTimeline } from '@/mocks/site';

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 | 瓜田黑料',
    url: 'https://xwatched.cn/about',
    inLanguage: 'zh-CN',
    description: site.description,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: site.name,
    url: 'https://xwatched.cn/',
    email: site.email,
    description: site.description,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xwatched.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://xwatched.cn/about' },
    ],
  },
];

export default function About() {
  return (
    <SiteLayout>
      <Seo
        title="关于我们 | 瓜田黑料内容定位与原则"
        description="了解瓜田黑料是谁、为什么做时间线复盘，以及我们在信息来源、事实与观点区分上的内容原则，还有读者的真实反馈。"
        keywords="关于瓜田黑料,内容原则,网站定位,娱乐舆论观察"
        path="/about"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7">
              <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                关于{site.name}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们不做情绪放大器，只做信息的整理者。把公开动态按时间排列，
                把情绪和事实分开呈现，让每一个热点都能被看清结构，而不是只看结论。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                {stats.map((item) => (
                  <div key={item.label}>
                    <span className="font-heading text-2xl font-bold text-primary-600">{item.value}</span>
                    <span className="mt-1 block text-xs text-foreground-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-4">
              <span className="text-xs font-medium tracking-[0.2em] text-primary-600">OUR STORY</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                我们为什么做这件事
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                一开始只是自己追热点时，想把事情弄明白。后来发现，
                大多数人缺的不是态度，而是一条排好的时间线。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ol className="space-y-6 border-l border-background-200 pl-6">
                {brandTimeline.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-background-50 bg-accent-500"></span>
                    <span className="font-heading text-sm font-bold text-primary-700">{item.year}</span>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100/50">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">PRINCIPLES</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              这四条是我们筛选信息时始终遵守的标准，也是我们判断一篇复盘是否合格的前提。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:mt-10">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="max-w-2xl">
            <span className="text-xs font-medium tracking-[0.2em] text-primary-600">READERS</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">读者怎么说</h2>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:mt-10">
            {testimonials.map((item) => (
              <figure key={item.name} className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                  <i className="ri-double-quotes-l text-sm"></i>
                </span>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">{item.text}</blockquote>
                <figcaption className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                  <span className="font-medium text-foreground-800">{item.name}</span>
                  <span className="hidden h-1 w-1 rounded-full bg-foreground-300 sm:block"></span>
                  <span className="whitespace-nowrap">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100/50">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:flex-row md:items-center md:justify-between md:p-8">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">想和我们聊聊？</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
                无论是线索投稿、内容纠错还是商务合作，都欢迎直接联系我们。一般会在两个工作日内回复。
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
              <Link
                to="/contact"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line"></i>
                前往联系页
              </Link>
              <a
                href={`mailto:${site.email}`}
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-6 py-3 text-sm text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-mail-line"></i>
                {site.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}